import { styles } from "../styles";
import { getPmStatus } from "../utils/pm";
import { hasCapability } from "../utils/capabilities";

// "Open" = any work order not yet completed or canceled. Mirrors the
// statuses used on the facility job board (requested, unassigned, assigned,
// in_progress).
const OPEN_STATUSES = new Set([
  "requested",
  "unassigned",
  "assigned",
  "in_progress",
]);

/**
 * Facility (customer) home — distinct from the contractor dashboard. A
 * facility tracks ITS OWN buildings, equipment, and whether maintenance is
 * actually happening. No contractor job board / tickets / POs / bills.
 * Buildings are already filtered to what this user may see (executive = all;
 * facilities_manager / maintenance = assigned), so the counts reflect that.
 */
export default function FacilityDashboard({
  customers = [],
  jobsRaw = [],
  publicJobsRaw = [],
  publicTicketsRaw = [],
  publicQuotesRaw = [],
  publicInvoicesRaw = [],
  messageThreadsRaw = [],
  notificationsRaw = [],
  dismissedNotificationIds = [],
  orgId,
  uid,
  role,
  capabilities,
  threadLastRead = {},
  canViewSignedTickets = false,
  onShowBuildings,
  onShowJobs,
  onShowRecentActivity,
  onShowSignedTickets,
  onShowMessages,
  onShowPmDueSoon,
  onShowPmOverdue,
  onShowQuotes,
  onShowInvoices,
  onShowEquipmentByStatus,
  facilityServiceTicketsRaw = [],
  onShowFacilityTickets,
}) {
  // Facility maintenance (tech tier) doesn't see Quotes or Recent Service
  // unless an executive grants the capability. Admin/manager tier defaults on.
  const canCreateQuotes = hasCapability(role, capabilities, "createQuotes");
  const canSeeRecentActivity = hasCapability(role, capabilities, "viewRecentActivity");

  const buildings = customers.flatMap((c) => c.locations || []);
  const totalBuildings = buildings.length;
  // Signed visits visible to this user (filtered down to buildings they can
  // see; mirror is already scoped via memberOrgs).
  const buildingIds = new Set(buildings.map((b) => b.id));
  const signedTicketCount = publicTicketsRaw.filter((t) =>
    buildingIds.has(t.locationId)
  ).length;
  // Open work orders = facility's own open jobs + contractor's open jobs on
  // the facility's buildings (so the executive sees the real workload).
  const openOwnJobs = jobsRaw.filter(
    (j) =>
      buildingIds.has(j.locationId) && OPEN_STATUSES.has(j.status || "unassigned")
  ).length;
  const openContractorJobs = publicJobsRaw.filter(
    (j) =>
      buildingIds.has(j.locationId) && OPEN_STATUSES.has(j.status || "unassigned")
  ).length;
  const openWorkOrderCount = openOwnJobs + openContractorJobs;
  // "Unread"-ish message count: threads whose last reply was from the other
  // side. (Per-user read tracking is a later refinement.)
  // "From someone else" = last message wasn't sent by THIS user. Works for
  // both broadcast building threads (cross-org) and intra-org DMs (where
  // sender and recipient share an org).
  // Count of UNREAD message notifications (matches the bell badge for
  // type=new_message). Each individual message lands as its own
  // notification, so 3 messages in 1 thread = 3 unread on the dashboard,
  // matching what you see in the bell.
  const dismissedSet = new Set(dismissedNotificationIds);
  const messagesFromOther = notificationsRaw.filter((n) => {
    if (n.type !== "new_message") return false;
    if (n.read) return false;
    if (dismissedSet.has(n.id)) return false;
    if (Array.isArray(n.targetUids) && n.targetUids.length > 0) {
      if (!uid || !n.targetUids.includes(uid)) return false;
    }
    return true;
  }).length;
  // Counter must match what the Quotes page actually shows (i.e.
  // respect this org's hide list). Otherwise badge says "1 waiting"
  // while the page filter hides that one.
  const openQuotesCount = publicQuotesRaw.filter(
    (q) =>
      q.status === "sent" && !(q.hiddenByOrgs || []).includes(orgId)
  ).length;

  const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const recentRecordCount = buildings
    .flatMap((b) =>
      (b.equipment || []).flatMap((eq) =>
        (eq.serviceRecords || []).filter(
          (r) =>
            !r.deleted &&
            new Date(r.createdAt || r.date).getTime() >= sevenDaysAgo
        )
      )
    ).length;

  // Equipment list denormalized with customer/location info so the
  // drill-in page can navigate to EquipmentDetailPage by setting all 3
  // selection states.
  const allEquipment = customers.flatMap((c) =>
    (c.locations || []).flatMap((l) =>
      (l.equipment || []).map((e) => ({
        ...e,
        customerId: c.id,
        customerName: c.name,
        locationId: l.id,
        locationName: l.name,
      }))
    )
  );
  const overduePmCount = allEquipment.filter(
    (eq) => getPmStatus(eq)?.overdue
  ).length;
  const dueSoonPmCount = allEquipment.filter(
    (eq) => getPmStatus(eq)?.dueSoon
  ).length;
  const greenCount = allEquipment.filter(
    (eq) => (eq.status || "green") === "green"
  ).length;
  const yellowCount = allEquipment.filter((eq) => eq.status === "yellow").length;
  const redCount = allEquipment.filter((eq) => eq.status === "red").length;

  // Pill-style health badges (matches the compact look on LocationDetailPage).
  // Yellow + Red are tappable; click opens the filtered equipment list.
  const healthTile = (label, count, bg, color, onClick) => (
    <div
      style={{
        padding: "6px 12px",
        borderRadius: "12px",
        background: bg,
        color,
        border: `1px solid ${color}`,
        fontSize: "13px",
        fontWeight: 600,
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        cursor: onClick && count > 0 ? "pointer" : "default",
      }}
      onClick={() => onClick && count > 0 && onClick()}
    >
      <span>● {label}</span>
      <strong style={{ fontSize: "15px" }}>{count}</strong>
    </div>
  );

  const tile = (label, value, sub, onClick) => (
    <div style={{ ...styles.card, cursor: "pointer" }} onClick={onClick}>
      <strong>{label}</strong>
      <div style={{ fontSize: "28px" }}>{value}</div>
      {sub && <div style={{ fontSize: "12px", color: "#666" }}>{sub}</div>}
    </div>
  );

  return (
    <>
      <h2>My Facility</h2>

      <h3 style={{ marginTop: 0, marginBottom: "8px" }}>Equipment Health</h3>
      <div
        style={{
          display: "flex",
          gap: "8px",
          flexWrap: "wrap",
          marginBottom: "20px",
        }}
      >
        {healthTile("Good", greenCount, "#c9efc9", "#1a7f37")}
        {healthTile("Caution", yellowCount, "#fff3cd", "#9a6b00", () =>
          onShowEquipmentByStatus &&
          onShowEquipmentByStatus("yellow", allEquipment)
        )}
        {healthTile("Disabled", redCount, "#f8d7da", "#c12c2c", () =>
          onShowEquipmentByStatus &&
          onShowEquipmentByStatus("red", allEquipment)
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: "12px",
          marginBottom: "20px",
        }}
      >
        {tile("Buildings", totalBuildings, "Your locations", onShowBuildings)}
        {tile(
          "Open Work Orders",
          openWorkOrderCount,
          "Not yet completed",
          onShowJobs
        )}
        {tile(
          "Service Tickets",
          facilityServiceTicketsRaw.filter((t) => !t.acknowledged).length,
          "Unacknowledged",
          onShowFacilityTickets
        )}
        {canSeeRecentActivity &&
          tile(
            "Recent Service",
            recentRecordCount,
            "Last 7 days",
            onShowRecentActivity
          )}
        {tile(
          "PM Due Soon",
          dueSoonPmCount,
          "Within 30 days",
          onShowPmDueSoon
        )}
        {tile("PM Overdue", overduePmCount, "Needs attention", onShowPmOverdue)}
        {canCreateQuotes &&
          tile(
            "Quotes",
            openQuotesCount,
            "Awaiting approval",
            onShowQuotes
          )}
        {tile(
          "Invoices",
          publicInvoicesRaw.length,
          "Ready to view / print",
          onShowInvoices
        )}
        {tile("Messages", messagesFromOther, "Unread", onShowMessages)}
        {canViewSignedTickets &&
          tile(
            "Signed Tickets",
            signedTicketCount,
            "Proof of work from contractor",
            onShowSignedTickets
          )}
      </div>
    </>
  );
}
